import EmptyDottedIcon from '@assets/icons/empty-dotted.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import { EmptyState } from '@experiences/components/empty-state';
import {
  ManagerProspectRowData,
  ManagerRepTimeRange,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/types';
import { partnerSupplier } from '@/utils/environment';
import {
  PROSPECT_STAGE_FILTER_ORDER,
  PROSPECT_STAGE_TRANSLATION_KEYS,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/constants';
import { ProspectsManagerFilters } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/prospects-manager-filters';
import { ProspectsManagerForecastTiles } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/prospects-manager-forecast-tiles';
import { ProspectsManagerPipelineSummary } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/prospects-manager-pipeline-summary';
import { ProspectsManagerRepsTimeRange } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/prospects-manager-reps-time-range';
import { ProspectsManagerView } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/prospects-manager-view';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesPortalProspectsStackParams } from '@/navigators/types';
import { SearchInput } from '@/experiences/sales-portal/components/search-input';
import { StackNavigationProp } from '@react-navigation/stack';
import { useFilteredManagerProspects } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/hooks/use-filtered-manager-prospects';
import { useManagerPipelineSummaryLoader } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/loaders/use-manager-pipeline-summary-loader';
import { useManagerProspectFilters } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/hooks/use-manager-prospect-filters';
import { useManagerProspectForecastLoader } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/loaders/use-manager-prospect-forecast-loader';
import { useManagerProspectsLoader } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/loaders/use-manager-prospects-loader';
import { useManagerRepSummariesLoader } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/loaders/use-manager-rep-summaries-loader';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '@/contexts/theme-context';

export const ProspectsManagerViewContent: React.FC = () => {
  const theme = useTheme();
  const navigation =
    useNavigation<StackNavigationProp<SalesPortalProspectsStackParams>>();

  const handleRowClick = useCallback(
    (row: ManagerProspectRowData) => {
      navigation.push(SALES_PORTAL_ROUTES.ProspectDetail, {
        restaurantUUID: row.restaurantUUID,
        supplierUUID: row.supplierUUID,
      });
    },
    [navigation],
  );

  const { repFilter, stageFilter, toggleRep, toggleStage } =
    useManagerProspectFilters();

  const { prospects, prospectsError, prospectsLoading, refetch } =
    useManagerProspectsLoader({
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
    });

  const { forecastError, forecastLoading, forecastTiles, refetchForecast } =
    useManagerProspectForecastLoader({
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      employeeUUIDs: repFilter,
      stages: stageFilter,
    });

  const { pipelineError, pipelineLoading, pipelineStages, refetchPipeline } =
    useManagerPipelineSummaryLoader({
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      employeeUUIDs: repFilter,
    });

  const [repTimeRange, setRepTimeRange] = useState(ManagerRepTimeRange.Mtd);

  const { refetchReps, reps, repsError, repsLoading } =
    useManagerRepSummariesLoader({
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      timeRange: repTimeRange,
    });

  const visibleReps = useMemo(
    () =>
      repFilter.length === 0
        ? reps
        : reps.filter((rep) => repFilter.includes(rep.repUUID)),
    [repFilter, reps],
  );

  const { appliedSearchText, filteredProspects, searchText, setSearchText } =
    useFilteredManagerProspects(prospects, { repFilter, stageFilter });

  const hasActiveFilters = repFilter.length > 0 || stageFilter.length > 0;

  const emptyStateCopy = useMemo(() => {
    if (appliedSearchText) {
      return {
        description: i18n.t('sales_portal.prospects.search_empty.description'),
        title: i18n.t('sales_portal.prospects.search_empty.title', {
          searchText: appliedSearchText,
        }),
      };
    }

    if (hasActiveFilters) {
      return {
        description: i18n.t(
          'sales_portal.prospects.manager.filter_empty.description',
        ),
        title: i18n.t('sales_portal.prospects.manager.filter_empty.title'),
      };
    }

    return {
      description: i18n.t('sales_portal.prospects.manager.empty.description'),
      title: i18n.t('sales_portal.prospects.empty.title'),
    };
  }, [appliedSearchText, hasActiveFilters]);

  const repsEmptyStateCopy = useMemo(() => {
    if (repFilter.length > 0) {
      return {
        description: i18n.t(
          'sales_portal.prospects.manager.reps_filter_empty.description',
        ),
        title: i18n.t('sales_portal.prospects.manager.reps_filter_empty.title'),
      };
    }

    return {
      description: i18n.t(
        'sales_portal.prospects.manager.reps_empty.description',
      ),
      title: i18n.t('sales_portal.prospects.manager.reps_empty.title'),
    };
  }, [repFilter]);

  const salesRepOptions = useMemo(() => {
    const byUUID = new Map<string, string>();
    prospects.forEach((prospect) => {
      if (prospect.repUUID) {
        byUUID.set(prospect.repUUID, prospect.repName);
      }
    });
    return [...byUUID.entries()]
      .sort(([, leftName], [, rightName]) => leftName.localeCompare(rightName))
      .map(([repUUID, repName]) => ({
        key: repUUID,
        label: repName,
        selected: repFilter.includes(repUUID),
      }));
  }, [prospects, repFilter]);

  const stageOptions = useMemo(
    () =>
      PROSPECT_STAGE_FILTER_ORDER.map((stage) => ({
        key: stage,
        label: i18n.t(PROSPECT_STAGE_TRANSLATION_KEYS[stage]),
        selected: stageFilter.includes(stage),
      })),
    [stageFilter],
  );

  return (
    <ProspectsManagerView
      filters={
        <ProspectsManagerFilters
          isRepFilterLoading={prospectsLoading}
          onToggleRep={toggleRep}
          onToggleStage={toggleStage}
          salesRepOptions={salesRepOptions}
          stageOptions={stageOptions}
        />
      }
      forecast={
        <ProspectsManagerForecastTiles
          hasError={Boolean(forecastError)}
          isLoading={forecastLoading}
          onRetry={refetchForecast}
          tiles={forecastTiles}
        />
      }
      hasProspectsError={Boolean(prospectsError)}
      hasRepsError={Boolean(repsError)}
      isProspectsLoading={prospectsLoading}
      isRepsLoading={repsLoading}
      onProspectRowClick={handleRowClick}
      onProspectsRetry={() => refetch()}
      onRepsRetry={refetchReps}
      pipelineSummary={
        <ProspectsManagerPipelineSummary
          hasError={Boolean(pipelineError)}
          isLoading={pipelineLoading}
          onRetry={refetchPipeline}
          stages={pipelineStages}
        />
      }
      prospects={filteredProspects}
      prospectsEmptyComponent={
        <EmptyState
          description={emptyStateCopy.description}
          iconElement={<EmptyDottedIcon stroke={theme.sk.color.icon.primary} />}
          title={emptyStateCopy.title}
        />
      }
      prospectsToolbar={
        <SearchInput
          onSearchChange={setSearchText}
          placeholderText={i18n.t(
            'sales_portal.prospects.manager.search_prospects',
          )}
          searchText={searchText}
        />
      }
      reps={visibleReps}
      repsEmptyComponent={
        <EmptyState
          description={repsEmptyStateCopy.description}
          iconElement={<EmptyDottedIcon stroke={theme.sk.color.icon.primary} />}
          title={repsEmptyStateCopy.title}
        />
      }
      repsToolbar={
        <ProspectsManagerRepsTimeRange
          onChange={setRepTimeRange}
          value={repTimeRange}
        />
      }
    />
  );
};
